/**
 * links — the CLIENT side of the per-run URL prefix-table link compression (KB-1768).
 *
 * The server (`functions/scripts/camp-data/_shared/runDocLinkTable.cjs`, applied by
 * `publishRunToFirestore.cjs` before the size guard) persists each member entry's `url`/`regUrl`
 * as `"<prefixIndex>|<suffix>"` against a top-level `runDoc.linkPrefixes: string[]` table, so the
 * KB-1739 reviewer links are cheap enough to KEEP on the largest runs (raleigh/cary) instead of
 * being stripped by the guard. A raw URL always starts with `http`, a compressed ref always with a
 * digit — the two shapes are unambiguous in both directions, so ONE expander handles every doc:
 *
 *   - legacy docs (raw `url`/`regUrl`, or stripped links) — values pass through untouched;
 *   - new docs — refs reassemble to `linkPrefixes[i] + suffix` at normalize time.
 *
 * An unresolvable ref (bad index, missing table — e.g. a doc written mid-deploy) expands to
 * `undefined`, i.e. an OMITTED link, never a dead one. Expansion happens at the normalizer boundary
 * (`coverage.ts` / `runCounts.ts`) so every downstream surface keeps receiving plain absolute URLs.
 *
 * Also home to the `session` link derivation (KB-1768 symptom 2): a drill-down row backed by a
 * known catalog offer carries only the offer's `docId` (~20 bytes); the production session-page URL
 * is derived here, never persisted.
 */

/** A compressed link ref: table index, `|`, suffix. Raw URLs start with `http` and never match. */
const COMPRESSED_LINK_RE = /^(\d+)\|/;

/**
 * The production session page a matched offer's `session` link opens. camp-data lives only in prod,
 * so the host is constant (the run doc never persists the full URL — only the docId).
 */
export const SESSION_PAGE_URL_BASE = "https://kidsbooked.com/session/";

/** The prod session-page URL for a matched offer's docId; undefined when there is no docId (no dead links). */
export function sessionUrlForDocId(docId: string | null | undefined): string | undefined {
  const id = String(docId ?? "").trim();
  return id ? `${SESSION_PAGE_URL_BASE}${id}` : undefined;
}

/** Expand one persisted link value against a prefix table (mirror of runDocLinkTable.expandLinkValue). */
export function expandLinkValue(v: unknown, linkPrefixes: readonly string[] | undefined): string | undefined {
  if (typeof v !== "string" || !v) return undefined;
  const m = COMPRESSED_LINK_RE.exec(v);
  if (!m) return v;
  const prefix = linkPrefixes?.[Number(m[1])];
  if (typeof prefix !== "string" || !prefix) return undefined;
  return prefix + v.slice(m[0].length);
}

/** A per-doc expander closure over the doc's `linkPrefixes` table (absent table ⇒ raw-passthrough only). */
export type LinkExpander = (v: unknown) => string | undefined;

/** Build the doc's expander from the loose run-doc `linkPrefixes` field (external Firestore JSON). */
export function makeLinkExpander(rawLinkPrefixes: unknown): LinkExpander {
  const table = Array.isArray(rawLinkPrefixes) ? rawLinkPrefixes.map((p) => String(p)) : undefined;
  return (v) => expandLinkValue(v, table);
}
